import { Badge } from '@/shared/ui/badge';
import { Send, CheckCircle2, Clock, AlertTriangle, RotateCcw } from 'lucide-react';

/**
 * Où en est la signature d'un mandat — un badge, une phrase, et le geste qui reste.
 *
 * ⚠️ IL N'INTERPRÈTE RIEN. Le code d'état vient du serveur
 * (`services/signature_mandat.etat_signature`), servi avec chaque mandat. Trois écrans
 * posaient la même question de trois façons, et deux d'entre eux répondaient FAUX depuis
 * le canal à distance : un mandat qui ATTEND le client s'affichait « Signé le - », en
 * vert, parce qu'ils lisaient `statut` (« ce mandat est en vigueur ») pour
 * `date_signature` (« quelqu'un l'a signé »). Recalculer ici rouvrirait la même porte
 * au quatrième écran.
 *
 * ⚠️ LA PHRASE COMPTE AUTANT QUE LE BADGE. « À envoyer au client » sans rien d'autre
 * laisse chercher quoi faire ; le `detail` nomme le geste. C'est la même règle que les
 * alertes de `sonde_signature`, qui ont dû apprendre à nommer un geste POSSIBLE : un
 * message qui décrit mal ce qu'il voit s'apprend à être ignoré.
 */
const CFG = {
  signe: {
    icone: CheckCircle2,
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    texte: 'text-neutral-500',
  },
  lien_envoye: {
    icone: Clock,
    badge: 'bg-sky-50 text-sky-700 border-sky-200',
    texte: 'text-neutral-500',
  },
  // Le lien n'est jamais arrivé : c'est une panne, et elle se répare d'un clic.
  lien_non_parti: {
    icone: AlertTriangle,
    badge: 'bg-red-50 text-red-700 border-red-200',
    texte: 'text-red-600',
  },
  // ⚠️ LE CAS QUE BOUBACAR A DEMANDÉ DE RENDRE VISIBLE. Ambre et non gris : un dossier
  // dont personne n'a envoyé le lien appelle un geste, il n'est pas au repos.
  a_envoyer: {
    icone: Send,
    badge: 'bg-amber-50 text-amber-800 border-amber-300',
    texte: 'text-amber-700',
  },
  canal_retire: {
    icone: RotateCcw,
    badge: 'bg-amber-50 text-amber-800 border-amber-300',
    texte: 'text-amber-700',
  },
};

function dateCourte(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('fr-FR',
      { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return ''; }
}

export default function EtatSignatureMandat({ etat, compact = false }) {
  if (!etat?.code) return null;
  const cfg = CFG[etat.code] || CFG.a_envoyer;
  const Icone = cfg.icone;
  const emis = dateCourte(etat.lien_emis_le);
  const expire = dateCourte(etat.lien_expire_a);

  return (
    <div className="space-y-1" data-testid={`etat-signature-${etat.code}`}>
      <Badge variant="outline" className={`${cfg.badge} text-[10px]`}>
        <Icone className="w-3 h-3 mr-1" />
        {etat.libelle}
      </Badge>
      {!compact && (
        <p className={`text-[11px] leading-snug ${cfg.texte}`}
           data-testid="etat-signature-detail">
          {etat.detail}
        </p>
      )}
      {/* QUAND, ET À QUELLE ADRESSE. Sans le destinataire, personne ne peut vérifier que
          le lien est parti à la bonne boîte — et c'est la première chose qu'on veut
          savoir quand un client « ne signe pas ». L'échéance dit jusqu'à quand ce lien
          vit : au-delà, il faut en émettre un nouveau, pas relancer. */}
      {etat.destinataire && (etat.code === 'lien_envoye' || etat.code === 'lien_non_parti') && (
        <p className="text-[11px] text-neutral-500" data-testid="etat-signature-envoi">
          {emis ? `Envoyé le ${emis} à ` : 'À '}
          <strong className="text-neutral-700">{etat.destinataire}</strong>
          {expire && <> — le lien expire le {expire}</>}
        </p>
      )}
    </div>
  );
}
